/* eslint-disable no-nested-ternary */
import { useEffect, useMemo } from 'react';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import { useLocation } from 'react-router';

import { FormItem } from 'components/shared';
import { validationRules } from 'helpers/utils';
import TextAreaElement from 'components/muiInputWrappers/TextAreaElement';
import { AutocompleteElement } from 'components/muiInputWrappers';
import useGetClients from 'hooks/useGetClients';

const path = '/admin/prioritySurveys';

function TitleForm({
  form,
  isEditable = true,
  titleModal
}: {
  form: UseFormReturn;
  isEditable?: boolean;
  titleModal?: string;
}) {
  const { t } = useTranslation('global');
  const { validateSpaces } = validationRules();

  const currentPath = useLocation().pathname;

  const { clients, isClientsLoading } = useGetClients();

  // Opciones de proceso disponibles
  const processOptions = useMemo(
    () => [
      // { label: t('surveyCriticality.process.ticket'), id: 1 } TODO: por el momento queda afuera,
      { label: t('surveyCriticality.process.problem'), id: 2 },
      { label: t('surveyCriticality.process.change'), id: 3 }
    ],
    [t]
  );

  // Sincronizar valores del formulario con objetos completos cuando las opciones estén disponibles
  useEffect(() => {
    // Sincronizar process
    const processId = form.getValues('process.id') as number | undefined;
    const currentProcess = form.getValues('process') as
      | { label: string; id: number }
      | undefined;
    if (
      processId &&
      (!currentProcess ||
        !currentProcess.label ||
        currentProcess.id !== processId)
    ) {
      const processOption = processOptions.find((opt) => opt.id === processId);
      if (processOption) {
        form.setValue('process', processOption);
      }
    }

    // Sincronizar clientId cuando los clientes estén disponibles
    const clientIdId = form.getValues('clientId.id') as number | undefined;
    const currentClientId = form.getValues('clientId') as
      | { label: string; id: number }
      | undefined;
    if (
      !isClientsLoading &&
      clients &&
      clients.length > 0 &&
      clientIdId &&
      (!currentClientId ||
        !currentClientId.label ||
        Number(currentClientId.id) !== Number(clientIdId))
    ) {
      const clientOption = clients.find(
        (client) => Number(client.id) === Number(clientIdId)
      );
      if (clientOption) {
        form.setValue('clientId', {
          label: clientOption.label,
          id: Number(clientOption.id)
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, processOptions, clients, isClientsLoading, currentPath]);

  return (
    <Grid marginTop={3} paddingX={3}>
      {currentPath === path ? (
        isEditable ? (
          <>
            <FormItem style={{ width: '100%' }}>
              <TextAreaElement
                form={form}
                maxLength={100}
                maxRows={2}
                name="title"
                placeholder={t('survey.template.withoutTitle')}
                size="small"
                validation={{ validate: validateSpaces }}
                variant="outlined"
                fullWidth
                required
              />
            </FormItem>
            <Grid marginBottom={2} marginTop={3}>
              <TextAreaElement
                form={form}
                maxLength={500}
                maxRows={4}
                name="description"
                placeholder={t('survey.template.descriptionOptional')}
                validation={{ validate: validateSpaces }}
              />
            </Grid>
          </>
        ) : (
          <>
            <Typography fontWeight="500" variant="h5">
              {form.getValues('title') ||
                titleModal ||
                t('survey.template.withoutTitle')}
            </Typography>
            <Typography variant="body1">
              {form.getValues('description')}
            </Typography>
          </>
        )
      ) : isEditable ? (
        <Grid paddingBottom="24px" spacing={2} container>
          <Grid md={6} xs={12} item>
            <FormItem style={{ width: '100%' }}>
              <TextAreaElement
                form={form}
                maxLength={100}
                maxRows={2}
                name="title"
                placeholder={t('survey.template.withoutTitle')}
                size="small"
                validation={{ validate: validateSpaces }}
                variant="outlined"
                fullWidth
                required
              />
            </FormItem>
            <FormItem style={{ width: '100%', marginTop: 30 }}>
              <TextAreaElement
                form={form}
                maxLength={500}
                maxRows={4}
                name="description"
                placeholder={t('survey.template.descriptionOptional')}
                validation={{ validate: validateSpaces }}
                fullWidth
              />
            </FormItem>
          </Grid>
          <Grid md={6} xs={12} item>
            <FormItem style={{ width: '100%' }}>
              <AutocompleteElement
                autocompleteProps={{
                  size: 'small'
                }}
                control={form.control}
                loading={false}
                name="process"
                options={processOptions}
                required
              />
            </FormItem>
            <FormItem style={{ width: '100%', marginTop: 30 }}>
              <AutocompleteElement
                autocompleteProps={{
                  size: 'small'
                }}
                control={form.control}
                loading={isClientsLoading}
                name="clientId"
                options={clients}
                required
              />
            </FormItem>
          </Grid>
        </Grid>
      ) : (
        <>
          <Typography fontWeight="500" variant="h5">
            {form.getValues('title') ||
              titleModal ||
              t('survey.template.withoutTitle')}
          </Typography>
          <Typography variant="body1">
            {form.getValues('description')}
          </Typography>
          <Typography variant="body1">
            {(form.getValues('process') as { label: string; id: number })
              ?.label || ''}
          </Typography>
          <Typography variant="body1">
            {(form.getValues('clientId') as { label: string; id: number })
              ?.label || ''}
          </Typography>
        </>
      )}
    </Grid>
  );
}

export default TitleForm;
